import { BUILT_IN_NAME, CUSTOMER_SEGMENTS, CUSTOMERS_BUILT_IN_NAME, defaultSidebar, isCustomersSidebar, removedKeyOf, removedKeyOfView, SIDEBAR_LIMITS, SIDEBAR_VIEWS, type AnySidebar, type CustomersBuiltIn, type SidebarBuiltIn } from "@omnabase/shared";

/** The sidebar and consent round (brief rule "The dialog"): the Customize dialog's edits as pure functions over a layout. Each answers a new
 *  layout and leaves the input as it was; the dialog hands the result to the store, which draws the column and saves it a moment later.
 *  The sidebar editor rework (2026-10-26, docs/briefs/sidebar-rework-round.md): an entry is named by its kind and id (`{ kind, id }`, unique in
 *  a layout), a group by its id, never by a position; every group is renamed and removed, every entry removed; a removed built-in group or
 *  view is remembered in the layout's `removed` list so the next read does not put it back.
 *  The customers sidebar round (2026-10-29, report findings 8 and 9): the same edits for the Customers column; each reads the column from the
 *  layout's own mark (`area: "customers"`): there a removed segment or customer tag is remembered (every customer tag is in the base), a
 *  project is not, and a blank built-in name becomes "Customers" or "Tags". */

type Item = { kind: string; id: string; hidden: boolean };
type Group = { id: string; name: string; builtIn?: string; hidden: boolean; folded: boolean; items: Item[] };
type Loose = { v: 1; area?: "customers"; groups: Group[]; removed?: string[] };
export type EntryRef = { kind: string; id: string };
const L = <T extends AnySidebar>(l: T) => l as unknown as Loose;
const back = <T extends AnySidebar>(l: Loose) => l as unknown as T;
const mapGroup = <T extends AnySidebar>(l: T, groupId: string, fn: (g: Group) => Group): T => back({ ...L(l), groups: L(l).groups.map((g) => (g.id === groupId ? fn(g) : g)) });
const sameItem = (a: EntryRef, b: EntryRef) => a.kind === b.kind && a.id === b.id;
const mapItem = <T extends AnySidebar>(l: T, ref: EntryRef, fn: (i: Item) => Item): T => back({ ...L(l), groups: L(l).groups.map((g) => (g.items.some((i) => sameItem(i, ref)) ? { ...g, items: g.items.map((i) => (sameItem(i, ref) ? fn(i) : i)) } : g)) });
const clamp = (n: number, max: number) => Math.max(0, Math.min(max, n));

/* ---------- the removal list ---------- */

/** the views today's inbox column holds: only their removal needs remembering (a view only the pool offers never comes back by itself) */
const BASE_VIEWS = new Set(defaultSidebar().groups.flatMap((g) => g.items.map((i) => i.id)));
/** what comes back by itself on the next read, so its removal is remembered: the inbox's base views; the Customers column's segments and
 *  customer tags (a project only the pool offers never comes back) */
const remembered = (l: Loose) => (i: EntryRef) => (l.area === "customers" ? i.kind === "segment" || i.kind === "customerTag" : i.kind === "view" && BASE_VIEWS.has(i.id));
const tombstonesOf = (l: Loose, items: readonly EntryRef[]) => items.filter(remembered(l)).map((i) => (i.kind === "view" ? removedKeyOfView(i.id) : removedKeyOf(i)));
/** the layout with these keys remembered as removed on purpose (no list when it is empty, so an untouched layout stays as it was) */
function withRemoved<T extends AnySidebar>(layout: T, add: readonly string[], drop: readonly string[] = []): T {
  const l = L(layout);
  const removed = [...new Set([...(l.removed ?? []), ...add])].filter((k) => !drop.includes(k));
  const { removed: _old, ...rest } = l;
  return back(removed.length ? { ...rest, removed } : rest);
}

/* ---------- groups ---------- */

export const setGroupHidden = <T extends AnySidebar>(l: T, groupId: string, hidden: boolean) => mapGroup(l, groupId, (g) => ({ ...g, hidden }));
export const toggleFolded = <T extends AnySidebar>(l: T, groupId: string) => mapGroup(l, groupId, (g) => ({ ...g, folded: !g.folded }));
/** any group's name as typed (`forSaving` gives a blank one a name) */
export const renameGroup = <T extends AnySidebar>(l: T, groupId: string, name: string) => mapGroup(l, groupId, (g) => ({ ...g, name: name.slice(0, SIDEBAR_LIMITS.nameMax) }));
/** a group dragged to place `toIndex` of the groups (its place after the move) */
export function moveGroupTo<T extends AnySidebar>(l: T, groupId: string, toIndex: number): T {
  const from = L(l).groups.findIndex((g) => g.id === groupId);
  if (from < 0) return l;
  const groups = [...L(l).groups]; const [g] = groups.splice(from, 1); groups.splice(clamp(toIndex, groups.length), 0, g!);
  return back({ ...L(l), groups });
}
/** a new group of one's own at the end, named "New group" until renamed */
export function addGroup<T extends AnySidebar>(l: T, id: string): T {
  return back({ ...L(l), groups: [...L(l).groups, { id, name: "New group", hidden: false, folded: false, items: [] }] });
}
/** any group goes with its entries; a built-in group and the built-in entries it held are remembered as removed */
export function removeGroup<T extends AnySidebar>(l: T, groupId: string): T {
  const g = L(l).groups.find((x) => x.id === groupId);
  if (!g) return l;
  const keys = [...(g.builtIn ? [`group:${g.builtIn}`] : []), ...tombstonesOf(L(l), g.items)];
  return withRemoved(back<T>({ ...L(l), groups: L(l).groups.filter((x) => x.id !== groupId) }), keys);
}

/* ---------- entries ---------- */

export const setItemHidden = <T extends AnySidebar>(l: T, ref: EntryRef, hidden: boolean) => mapItem(l, ref, (i) => ({ ...i, hidden }));
/** an entry to place `toIndex` of a group, its own or another (its place after the move; past the end means last) */
export function moveEntry<T extends AnySidebar>(l: T, ref: EntryRef, toGroup: string, toIndex: number): T {
  const item = L(l).groups.flatMap((g) => g.items).find((i) => sameItem(i, ref));
  if (!item || !L(l).groups.some((g) => g.id === toGroup)) return l;
  return back({ ...L(l), groups: L(l).groups.map((g) => {
    const items = g.items.filter((i) => !sameItem(i, ref));
    if (g.id === toGroup) items.splice(clamp(toIndex, items.length), 0, item);
    return items.length === g.items.length && g.id !== toGroup ? g : { ...g, items };
  }) });
}
/** an entry from the pool to the end of a group, shown (nothing when it is placed already); its old removal is forgotten */
export function addItem<T extends AnySidebar>(l: T, groupId: string, item: EntryRef): T {
  if (L(l).groups.some((g) => g.items.some((i) => sameItem(i, item)))) return l;
  const added = mapGroup(l, groupId, (g) => ({ ...g, items: [...g.items, { kind: item.kind, id: item.id, hidden: false }] }));
  return withRemoved(added, [], [item.kind === "view" ? removedKeyOfView(item.id) : removedKeyOf(item)]);
}
/** any entry out; a built-in one is remembered as removed */
export function removeItem<T extends AnySidebar>(l: T, ref: EntryRef): T {
  const item = L(l).groups.flatMap((g) => g.items).find((i) => sameItem(i, ref));
  if (!item) return l;
  return withRemoved(back<T>({ ...L(l), groups: L(l).groups.map((g) => ({ ...g, items: g.items.filter((i) => !sameItem(i, ref)) })) }), tombstonesOf(L(l), [item]));
}

/* ---------- the pool, and what is saved ---------- */

/** what the pool offers a group, matching the search: in the inbox the views not placed yet, then the tags not placed yet, by name; in the
 *  Customers column the segments, the customer tags by name and the projects (`projects`, the open ones) not placed yet, never an inbox view
 *  or a ticket tag */
export type PoolChoice = { kind: string; id: string; label: string; icon: string };
type PoolTag = { id: string; name: string; kind?: string };
export function poolChoices(l: AnySidebar, tags: readonly PoolTag[], search: string, o: { projects?: readonly { id: string; name: string }[] } = {}): PoolChoice[] {
  const placed = new Set(L(l).groups.flatMap((g) => g.items.map((i) => `${i.kind}:${i.id}`)));
  const byName = [...tags].sort((a, b) => a.name.localeCompare(b.name));
  const all: PoolChoice[] = isCustomersSidebar(l) ? [
    ...CUSTOMER_SEGMENTS.map((s) => ({ kind: "segment", id: s.id, label: s.label, icon: s.icon })),
    ...byName.filter((t) => t.kind === "customer").map((t) => ({ kind: "customerTag", id: t.id, label: t.name, icon: "tag" })),
    ...(o.projects ?? []).map((p) => ({ kind: "project", id: p.id, label: p.name, icon: "package" })),
  ] : [
    ...Object.entries(SIDEBAR_VIEWS).map(([id, v]) => ({ kind: "view", id, label: v.label, icon: v.icon })),
    ...byName.map((t) => (t.kind === "customer" ? { kind: "customerTag", id: t.id, label: t.name, icon: "user" } : { kind: "tag", id: t.id, label: t.name, icon: "tag" })),
  ];
  const q = search.trim().toLowerCase();
  return all.filter((c) => !placed.has(`${c.kind}:${c.id}`) && (!q || c.label.toLowerCase().includes(q)));
}

/** the name a built-in group goes by (either column) */
export const builtInName = (builtIn: string): string => (BUILT_IN_NAME as Record<string, string>)[builtIn as SidebarBuiltIn] ?? (CUSTOMERS_BUILT_IN_NAME as Record<string, string>)[builtIn as CustomersBuiltIn] ?? "New group";
/** what is saved: a blank name becomes the built-in group's own name, or "New group" for a group of one's own (the server refuses an empty
 *  name) */
export const forSaving = <T extends AnySidebar>(l: T): T => back({ ...L(l), groups: L(l).groups.map((g) => ({ ...g, name: g.name.trim() || (g.builtIn ? builtInName(g.builtIn) : "New group") })) });
